import { useEffect, useRef, useState } from "react";

import { ProductKey } from "@/components/signup/utils";
import {
    NBPUserRecommendationProduct,
    GrowthUnifiedProfile
} from "@/libraries/growth-unified-profile-client";
import { useGupsStatus } from "@/templates/ondemand/signup/confirmation/confirmation-page/src/hooks/use-gups";
import { getLocalStorage, setLocalStorage } from "@/utils/localstorage";
import { useLoggedInUser } from "@/utils/logged-in-user-provider";

import { useUserContext } from "../user-context-provider";

/** Long TTL so hero exposure/action keys behave like persistent flags (matches prior raw localStorage behavior). */
const XFLOW_ML_LS_TTL_SEC = 10 * 365 * 24 * 60 * 60;

export const useUserTeamTypeForXflow = () => {
    const { getUserProfileJobFunctionCallback } = useGupsStatus();
    const [getJobFunction, setJobFunction] = useState<string | undefined>(
        undefined
    );

    useEffect(() => {
        const getTeamType = async () => {
            const result = await getUserProfileJobFunctionCallback(
                "homepage-xflow-job-function"
            );
            const jobFunction = (
                result as { data?: GrowthUnifiedProfile["userProfile"] }
            )?.data?.jobFunction;
            setJobFunction(jobFunction);
        };

        getTeamType();
    }, [getUserProfileJobFunctionCallback]);

    return getJobFunction;
};

/** Allowed product keys for NBP xflow recommendation (normalized display names). */
const XFLOW_ALLOWED_PRODUCTS = new Set([
    "jira",
    "jira service management",
    "jira product discovery",
    "loom",
    "confluence"
]);

/** Default recommendation when no job-function-specific order applies. */
const XFLOW_DEFAULT_PRODUCT_ORDER = [
    "confluence",
    "loom",
    "jira service management",
    "jira product discovery",
    "jira"
] as const;

/** Job function → recommended product order (first product user doesn't have is chosen). */
const JOB_FUNCTION_PRODUCT_ORDER: Record<string, readonly string[]> = {
    "engineering": [
        "jira service management",
        "confluence",
        "jira product discovery",
        "loom",
        "jira"
    ],
    "information technology": [
        "jira service management",
        "confluence",
        "jira product discovery",
        "loom",
        "jira"
    ],
    "operations": [
        "jira service management",
        "confluence",
        "jira product discovery",
        "loom",
        "jira"
    ],
    "customer service": [
        "jira service management",
        "loom",
        "confluence",
        "jira",
        "jira product discovery"
    ],
    "human resources": [
        "jira service management",
        "confluence",
        "loom",
        "jira product discovery",
        "jira"
    ],
    "data science": [
        "jira service management",
        "confluence",
        "jira product discovery",
        "loom",
        "jira"
    ],
    "product management": [
        "jira product discovery",
        "confluence",
        "jira service management",
        "loom",
        "jira"
    ],
    "program management": [
        "confluence",
        "jira product discovery",
        "loom",
        "jira service management",
        "jira"
    ],
    "legal": [
        "confluence",
        "jira service management",
        "loom",
        "jira",
        "jira product discovery"
    ]
};

export interface ExistingSitesFlags {
    userExistingSites: unknown[] | undefined;
    hasJiraSite: boolean;
    hasJSMSite: boolean;
    hasJPDSite: boolean;
    hasLoomSite: boolean;
    hasConfluenceSite: boolean;
}

export function getExistingSitesFlags(profile: {
    attributes?: { existingSites?: { products?: { productKey: string }[] }[] };
}): ExistingSitesFlags {
    const userExistingSites = profile.attributes?.existingSites;
    return {
        userExistingSites,
        hasJiraSite: Boolean(
            userExistingSites?.some((site) =>
                site.products?.some(
                    (p) => p.productKey === ProductKey.JIRA_SOFTWARE
                )
            )
        ),
        hasJSMSite: Boolean(
            userExistingSites?.some((site) =>
                site.products?.some(
                    (p) => p.productKey === ProductKey.JIRA_SERVICE_MANAGEMENT
                )
            )
        ),
        hasJPDSite: Boolean(
            userExistingSites?.some((site) =>
                site.products?.some(
                    (p) => p.productKey === ProductKey.JIRA_PRODUCT_DISCOVERY
                )
            )
        ),
        hasLoomSite: Boolean(
            userExistingSites?.some((site) =>
                site.products?.some((p) => p.productKey === ProductKey.LOOM)
            )
        ),
        hasConfluenceSite: Boolean(
            userExistingSites?.some((site) =>
                site.products?.some(
                    (p) => p.productKey === ProductKey.CONFLUENCE
                )
            )
        )
    };
}

/** Picks first recommended product user doesn't already have, by job function; falls back to "jira". */
export function getRecommendationByJobFunction(
    jobFunction: string | undefined,
    flags: ExistingSitesFlags
): string {
    const order: readonly string[] =
        (jobFunction
            ? JOB_FUNCTION_PRODUCT_ORDER[jobFunction.toLowerCase()]
            : undefined) ?? XFLOW_DEFAULT_PRODUCT_ORDER;
    const hasByProduct: Record<string, boolean> = {
        "jira": flags.hasJiraSite,
        "jira service management": flags.hasJSMSite,
        "jira product discovery": flags.hasJPDSite,
        "loom": flags.hasLoomSite,
        "confluence": flags.hasConfluenceSite
    };
    const recommended = order.find((p: string) => !hasByProduct[p]);
    return recommended ?? "jira";
}

function normalizeProductLabel(s: string): string {
    return s.toLowerCase().trim().replace(/\s+/g, " ");
}

/** Filters NBP v3 values to allowed products, optionally excluding products the user already has. */
function filterNbpV3Recommendations(
    values: string[],
    excluded: Set<string>
): string[] {
    return values.filter((product) => {
        const n = normalizeProductLabel(product);
        return XFLOW_ALLOWED_PRODUCTS.has(n) && !excluded.has(n);
    });
}

/** Set of product keys the user already has (for excluding from NBP v3 list). */
function getExcludedProductSet(flags: ExistingSitesFlags): Set<string> {
    const excluded = new Set<string>();
    if (flags.hasJiraSite) excluded.add("jira");
    if (flags.hasJSMSite) excluded.add("jira service management");
    if (flags.hasJPDSite) excluded.add("jira product discovery");
    if (flags.hasLoomSite) excluded.add("loom");
    if (flags.hasConfluenceSite) excluded.add("confluence");
    return excluded;
}

const XFLOW_ML_HERO_EXPOSURE_KEY = (accountId: string) =>
    `wac.xflow.ml.hero.exposure.${accountId}`;
const XFLOW_ML_HERO_ACTION_KEY = (accountId: string) =>
    `wac.xflow.ml.hero.actionTaken.${accountId}`;

function readLocalStorageSafe(key: string): string | null {
    return getLocalStorage(key, true);
}

function writeLocalStorageSafe(key: string, value: string): void {
    setLocalStorage(key, value, XFLOW_ML_LS_TTL_SEC);
}

/** @internal exposed for tests */
export function readXflowMlHeroExposureCount(accountId: string): number {
    const raw = readLocalStorageSafe(XFLOW_ML_HERO_EXPOSURE_KEY(accountId));
    const n = raw !== null ? Number.parseInt(raw, 10) : 0;
    return Number.isFinite(n) && n >= 0 ? n : 0;
}

function incrementXflowMlHeroExposure(accountId: string): number {
    const next = readXflowMlHeroExposureCount(accountId) + 1;
    writeLocalStorageSafe(XFLOW_ML_HERO_EXPOSURE_KEY(accountId), String(next));
    return next;
}

export function hasXflowMlHeroActionTaken(accountId: string): boolean {
    return readLocalStorageSafe(XFLOW_ML_HERO_ACTION_KEY(accountId)) === "1";
}

/** Call when the user clicks Get started or Contact sales on the ML crossflow hero. */
export function markXflowMlHeroActionTaken(accountId: string): void {
    writeLocalStorageSafe(XFLOW_ML_HERO_ACTION_KEY(accountId), "1");
}

/**
 * Resolves the ML recommendation string from API result, flags, and job function.
 * Pure function: no side effects, single return path per case.
 * @param listIndex - 0 = first filtered NBP v3 value; 1 = next value when escalating after repeat exposures.
 */
function resolveMlRecommendation(
    data: { nbp?: NBPUserRecommendationProduct[] } | null | undefined,
    error: unknown,
    flags: ExistingSitesFlags,
    jobFunction: string | undefined,
    listIndex: 0 | 1 = 0
): string {
    const fallback = () =>
        jobFunction
            ? getRecommendationByJobFunction(jobFunction, flags)
            : "jira";

    if (error) return fallback();

    const nbpV3 = data?.nbp?.find(
        (item: NBPUserRecommendationProduct) => item.key === "v3"
    );
    if (!nbpV3?.values?.length) return fallback();

    const excluded = getExcludedProductSet(flags);
    const filtered = flags.userExistingSites?.length
        ? filterNbpV3Recommendations(nbpV3.values, excluded)
        : filterNbpV3Recommendations(nbpV3.values, new Set());

    const picked = filtered[listIndex];
    if (picked !== undefined) {
        return picked;
    }
    return fallback();
}

/**
 * determines the recommended product for the user based on the job function and the existing sites. Default return back to Jira
 * @returns string | undefined
 */
export const useMlBasedXflowRecommendedProduct = () => {
    const { getAccountProfileRecommendationsCallback } = useGupsStatus();
    const { profile } = useUserContext();
    const { accountId } = useLoggedInUser();
    const jobFunction = useUserTeamTypeForXflow();
    const [mlBasedXflowRecommendedProduct, setMlBasedXflowRecommendedProduct] =
        useState<string | undefined>(undefined);
    /** One exposure bump per mount + accountId (avoids double-counting on dependency updates). */
    const exposureBumpForAccountRef = useRef<string | null>(null);

    useEffect(() => {
        if (profile.requestInProgress) return;
        if (accountId === undefined) return;
        const idForRecommendations: string = accountId;

        if (typeof window !== "undefined") {
            if (exposureBumpForAccountRef.current !== idForRecommendations) {
                exposureBumpForAccountRef.current = idForRecommendations;
                incrementXflowMlHeroExposure(idForRecommendations);
            }
        }

        async function loadRecommendation() {
            const flags = getExistingSitesFlags(profile);
            const {
                data,
                error,
                requestInProgress: recommendationsLoading
            } = await getAccountProfileRecommendationsCallback(
                idForRecommendations,
                "homepage-xflow-ml-rec"
            );

            if (recommendationsLoading) {
                return;
            }

            if (error) {
                console.error(
                    "Error getting account profile recommendations",
                    error
                );
            }

            let listIndex: 0 | 1 = 0;
            if (typeof window !== "undefined") {
                const exposureCount =
                    readXflowMlHeroExposureCount(idForRecommendations);
                const actionTaken =
                    hasXflowMlHeroActionTaken(idForRecommendations);
                if (exposureCount > 2 && !actionTaken) {
                    listIndex = 1;
                }
            }

            setMlBasedXflowRecommendedProduct(
                resolveMlRecommendation(
                    data,
                    error,
                    flags,
                    jobFunction,
                    listIndex
                )
            );
        }

        loadRecommendation();
    }, [
        getAccountProfileRecommendationsCallback,
        profile?.attributes?.existingSites,
        profile?.requestInProgress,
        accountId,
        jobFunction
    ]);

    return mlBasedXflowRecommendedProduct;
};
